import React from 'react'
import { DocumentNode } from 'graphql'
import { PureQueryOptions } from 'apollo-client'
import { AggregationOptions } from '../aggregations'
import {
  VulnerabilitiesSeverityChartLoader,
  VulnerabilitySeveritySnapshotBreakdown
} from './vulnerabilities-severity-chart-loader'
import { DateRangeOption } from '../../legacy-design-system/date-picker'
import {
  DateRange,
  dayInMillis
} from '../../legacy-design-system/date-picker/shared'
import {
  VulnerabilitiesDiscoveryChartLoader,
  VulnerabilityDiscoverySnapshotBreakdown
} from './vulnerabilities-discovery-chart-loader'

type SnapshotIntervalType = 'HOUR' | 'DAY' | 'MONTH'

interface ChildProps {
  readonly severityChartSnapshots?: VulnerabilitySeveritySnapshotBreakdown[]
  readonly discoveryChartSnapshots?: VulnerabilityDiscoverySnapshotBreakdown[]
  readonly refetchQueries: PureQueryOptions[]
  readonly loadingSnapshots: boolean
}

interface Props<TAggregationCategory extends string> {
  // Query Configuration
  readonly vulnerabilitiesSeverityChartQuery: DocumentNode
  readonly vulnerabilitiesDiscoveryChartQuery: DocumentNode

  readonly organizationID: string
  readonly interval: SnapshotIntervalType

  // Filters
  readonly filterText: string
  readonly quickFilterID: string
  readonly aggregationFilters: AggregationOptions<TAggregationCategory>
  readonly additionalFilters?: { readonly [key: string]: any }

  readonly refetchQueries: PureQueryOptions[]
  readonly children: (props: ChildProps) => React.ReactNode
}

export function ChartLoader<TAggregationCategory extends string>({
  vulnerabilitiesSeverityChartQuery,
  vulnerabilitiesDiscoveryChartQuery,
  organizationID,
  interval,
  filterText,
  quickFilterID,
  aggregationFilters,
  additionalFilters,
  children,
  refetchQueries
}: Props<TAggregationCategory>) {
  return (
    <VulnerabilitiesSeverityChartLoader
      query={vulnerabilitiesSeverityChartQuery}
      organizationID={organizationID}
      interval={interval}
      quickFilter={quickFilterID}
      filterText={filterText}
      aggregationFilters={aggregationFilters}
      additionalFilters={additionalFilters}
    >
      {({
        queryInfo: vulnerabilitiesSeverityChartQueryInfo,
        severityChartSnapshots,
        loading: loadingSeveritySnapshots
      }) => {
        return (
          <VulnerabilitiesDiscoveryChartLoader
            query={vulnerabilitiesDiscoveryChartQuery}
            organizationID={organizationID}
            interval={interval}
            quickFilter={quickFilterID}
            filterText={filterText}
            aggregationFilters={aggregationFilters}
            additionalFilters={additionalFilters}
          >
            {({
              queryInfo: vulnerabilitiesDiscoveryChartQueryInfo,
              discoveryChartSnapshots,
              loading: loadingDiscoverySnapshots
            }) => {
              return children({
                severityChartSnapshots,
                discoveryChartSnapshots,
                refetchQueries: [
                  ...refetchQueries,
                  vulnerabilitiesSeverityChartQueryInfo,
                  vulnerabilitiesDiscoveryChartQueryInfo
                ],
                loadingSnapshots:
                  loadingSeveritySnapshots || loadingDiscoverySnapshots
              })
            }}
          </VulnerabilitiesDiscoveryChartLoader>
        )
      }}
    </VulnerabilitiesSeverityChartLoader>
  )
}

export const getChartSnapshotInterval = (
  dateRangeOption?: DateRangeOption,
  dateRange?: DateRange
): SnapshotIntervalType => {
  switch (dateRangeOption) {
    case DateRangeOption.LAST_DAY:
      return 'HOUR'
    case DateRangeOption.LAST_7_DAYS:
      return 'DAY'
    case DateRangeOption.LAST_30_DAYS:
      return 'DAY'
    case DateRangeOption.LAST_12_MONTHS:
      return 'MONTH'
    case DateRangeOption.CUSTOM:
      if (
        !!dateRange &&
        dateRange.end.getTime() - dateRange.start.getTime() > 0 &&
        dateRange.end.getTime() - dateRange.start.getTime() <= dayInMillis * 5
      ) {
        return 'HOUR'
      }
      return 'DAY'
    default:
      return 'HOUR'
  }
}
